/**
 * File system utilities
 */

import { promises as fs } from 'fs';
import { dirname, join } from 'path';
import type { GeneratedFile } from './types.js';

/**
 * Check if a directory exists
 */
export async function directoryExists(path: string): Promise<boolean> {
  try {
    const stats = await fs.stat(path);
    return stats.isDirectory();
  } catch {
    return false;
  }
}

/**
 * Check if a file exists
 */
export async function fileExists(path: string): Promise<boolean> {
  try {
    const stats = await fs.stat(path);
    return stats.isFile();
  } catch {
    return false;
  }
}

/**
 * Create directory recursively
 */
export async function ensureDirectory(path: string): Promise<void> {
  try {
    await fs.mkdir(path, { recursive: true });
  } catch (error) {
    throw new Error(`Failed to create directory: ${path}`);
  }
}

/**
 * Write file, creating parent directories if needed
 */
export async function writeFile(path: string, content: string): Promise<void> {
  try {
    const dir = dirname(path);
    await ensureDirectory(dir);
    await fs.writeFile(path, content, 'utf-8');
  } catch (error) {
    throw new Error(`Failed to write file: ${path}`);
  }
}

/**
 * Write multiple files
 */
export async function writeFiles(
  basePath: string,
  files: GeneratedFile[]
): Promise<void> {
  for (const file of files) {
    const fullPath = join(basePath, file.path);
    await writeFile(fullPath, file.content);
  }
}

/**
 * Copy file
 */
export async function copyFile(src: string, dest: string): Promise<void> {
  try {
    const content = await fs.readFile(src, 'utf-8');
    await writeFile(dest, content);
  } catch (error) {
    throw new Error(`Failed to copy file from ${src} to ${dest}`);
  }
}

